// CP2020 EV (encumbrance) computation — the second consumer of ArmorLayerSorter (SP
// stacking is the other). EV is a POSITIVE magnitude: bigger = worse, all terms add.
// Full rules + worked examples: docs/cp2020-armor-math.md.
//
//   EV = nativeSum + upperZoneMax + lowerZoneMax + headZoneMax
//
// - nativeSum: Σ each active piece's own `ev` (whole character, each once, head included).
// - Per ZONE (upper = torso+arms, lower = legs, head = its own single-location zone), each
//   LOCATION gets a penalty = coreLayering + cb4, computed over that location's EV-mode layers
//   (skinweave ejected, integral counts — ArmorLayerSorter.layersFor(loc,"ev")). The zone
//   contributes the MAX over its locations; the per-location pass only exists to catch a worse
//   outlier, so max makes it effectively per-zone. Head is a single location, so its "max" is
//   just that location's penalty (head-covering clothing — helmet/hood/bodysuit — layers too).
//
// coreLayering(loc): by layer COUNT — 1→0, 2→1, 3→2 (= max(0, count-1)). Head caps at 2 layers
//   (the sorter enforces it), so head core tops out at 1.
// cb4(loc): weight-class over-layering with a free baseline of 2 substitutable slots per zone
//   (each accepts its cap OR LIGHTER: torso+head [≤heavy, ≤light], legs [≤heavy, ≤medium]).
//   Layers beyond the free slots are PAID by their own material. Assignment is greedy to
//   MINIMIZE paid EV: free the two heaviest pieces that legally fit the slots; pay for the rest.
//   (The rulebook CB4 table only specifies torso + legs; head REUSES the torso tier.)
//
// Invalid loadout (sorter refuses) → { ev: null, invalid: true }: EV can't be computed for an
// illegal configuration (fail-loud, matching the sorter). Callers render the invalid state.

import { ArmorLayerSorter, WEIGHTS } from "#lib/armor_layers"

const UPPER = ["torso", "rarm", "larm"]
const LOWER = ["rleg", "lleg"]
const HEAD = ["head"]

// Per-zone CB4 config: the two free slots' weight CAPS (a slot takes its cap or lighter) and
// the per-material PAID cost for extra layers. Keyed by zone. Head is not in the rulebook CB4
// table — it reuses the torso (upper) tier.
const CB4 = {
  upper: { slots: ["heavy", "light"], cost: { light: 1, medium: 3, heavy: 4 } },
  lower: { slots: ["heavy", "medium"], cost: { light: 1, medium: 2, heavy: 3 } },
  head: { slots: ["heavy", "light"], cost: { light: 1, medium: 3, heavy: 4 } },
}

const rank = (w) => WEIGHTS[w] ?? WEIGHTS.medium
const fits = (weight, cap) => rank(weight) <= rank(cap)

// core layering penalty from a layer count: 1st layer free, each further layer +1.
function coreLayering(count) {
  return Math.max(0, count - 1)
}

// CB4 over-layer penalty for one location's layers, in a given zone. Greedily assign the two
// free slots to the heaviest pieces that fit, then sum the paid cost of the rest.
function cb4(layers, zone) {
  const { slots, cost } = CB4[zone]
  // Heaviest-first: the heaviest piece is most expensive to pay for, so freeing it saves most.
  const remaining = layers
    .map((p) => p.weight)
    .sort((a, b) => rank(b) - rank(a))

  // Fill each free slot (in the order given: heavy-cap first) with the heaviest still-unfreed
  // piece that fits it. A freed piece costs nothing; everything left is paid.
  for (const cap of slots) {
    const idx = remaining.findIndex((w) => fits(w, cap))
    if (idx !== -1) remaining.splice(idx, 1)
  }
  return remaining.reduce((sum, w) => sum + (cost[w] ?? 0), 0)
}

// The max per-location penalty (core + cb4) over a zone's locations.
function zonePenalty(sorter, locations, zone) {
  let max = 0
  for (const loc of locations) {
    const layers = sorter.layersFor(loc, "ev")
    const penalty = coreLayering(layers.length) + cb4(layers, zone)
    if (penalty > max) max = penalty
  }
  return max
}

// pieces: the sorter's piece shape ({ sp, weight, isHard, isSkinWeave, isIntegral, locations })
// PLUS `ev` (native encumbrance) and `active` filtering already applied by the caller.
export function computeEV(pieces) {
  const list = Array.isArray(pieces) ? pieces : []
  const sorter = new ArmorLayerSorter(list)
  if (!sorter.valid) return { ev: null, invalid: true, invalidReason: sorter.invalidReason }

  const nativeSum = list.reduce((sum, p) => sum + (Number(p.ev) || 0), 0)
  const upper = zonePenalty(sorter, UPPER, "upper")
  const lower = zonePenalty(sorter, LOWER, "lower")
  const head = zonePenalty(sorter, HEAD, "head")
  return { ev: nativeSum + upper + lower + head, invalid: false }
}

// Exported for tests.
export { coreLayering, cb4, zonePenalty, UPPER, LOWER, HEAD, CB4 }
